import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, Volume2, Mic, MicOff } from "lucide-react";
import { type Difficulty, getDifficultyConfig } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

const shuffleArray = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

type Question = {
  question: string;
  image: string;
  options: string[];
  answer: string;
};

const ALL_QUESTIONS: Question[] = [
  // الحيوانات
  { question: "ما أسرع حيوان في العالم؟", image: "🐆", options: ["الفهد", "الأسد", "الحصان", "النسر"], answer: "الفهد" },
  { question: "أين يعيش الدب القطبي؟", image: "🐻‍❄️", options: ["القطب الشمالي", "الصحراء", "الغابة", "البحر"], answer: "القطب الشمالي" },
  { question: "كم رجل للعنكبوت؟", image: "🕷️", options: ["٨ أرجل", "٦ أرجل", "٤ أرجل", "١٠ أرجل"], answer: "٨ أرجل" },
  { question: "ماذا يأكل الباندا؟", image: "🐼", options: ["الخيزران (البامبو)", "اللحوم", "الأسماك", "الفواكه فقط"], answer: "الخيزران (البامبو)" },
  { question: "أي حيوان يضع البيض ولا يطير؟", image: "🐧", options: ["البطريق", "العصفور", "النسر", "الببغاء"], answer: "البطريق" },
  { question: "ما الحيوان الذي ينام واقفاً؟", image: "🐴", options: ["الحصان", "القط", "الكلب", "الأرنب"], answer: "الحصان" },
  { question: "كم قلب للأخطبوط؟", image: "🐙", options: ["٣ قلوب", "قلب واحد", "٢ قلبان", "٥ قلوب"], answer: "٣ قلوب" },
  // النباتات
  { question: "ماذا تحتاج النباتات لتنمو؟", image: "🌱", options: ["ماء وشمس وتربة", "ماء فقط", "هواء فقط", "ظلام وبرد"], answer: "ماء وشمس وتربة" },
  { question: "ما الجزء الذي يمتص الماء من التربة؟", image: "🌿", options: ["الجذور", "الأوراق", "الساق", "الزهرة"], answer: "الجذور" },
  { question: "ما وظيفة الأوراق الخضراء؟", image: "🍃", options: ["صنع الغذاء بالضوء", "امتصاص الماء", "حمل الثمار", "حماية الجذع"], answer: "صنع الغذاء بالضوء" },
  { question: "ما أكبر زهرة في العالم؟", image: "🌺", options: ["رافليسيا", "الورد", "عباد الشمس", "التوليب"], answer: "رافليسيا" },
  { question: "من أين تأتي الفواكه؟", image: "🍎", options: ["من الأزهار بعد التلقيح", "من الجذور", "من الأوراق", "من الساق"], answer: "من الأزهار بعد التلقيح" },
  // الفضاء
  { question: "ما أقرب نجم إلى الأرض؟", image: "☀️", options: ["الشمس", "القمر", "المريخ", "المشتري"], answer: "الشمس" },
  { question: "كم كوكب في المجموعة الشمسية؟", image: "🪐", options: ["٨ كواكب", "٦ كواكب", "١٠ كواكب", "٥ كواكب"], answer: "٨ كواكب" },
  { question: "ما الكوكب الأحمر؟", image: "🔴", options: ["المريخ", "المشتري", "زحل", "الزهرة"], answer: "المريخ" },
  { question: "ما هو القمر؟", image: "🌙", options: ["تابع طبيعي للأرض", "كوكب صغير", "نجم بعيد", "مذنب"], answer: "تابع طبيعي للأرض" },
  { question: "لماذا نرى النجوم ليلاً فقط؟", image: "⭐", options: ["ضوء الشمس يخفيها نهاراً", "تختفي نهاراً", "تنام نهاراً", "تبتعد نهاراً"], answer: "ضوء الشمس يخفيها نهاراً" },
  // الطقس والطبيعة
  { question: "لماذا تمطر السماء؟", image: "🌧️", options: ["تكاثف بخار الماء", "القمر يسكب ماء", "الرياح تحمل البحر", "الشمس تبكي"], answer: "تكاثف بخار الماء" },
  { question: "ما سبب ظهور قوس قزح؟", image: "🌈", options: ["انكسار الضوء في الماء", "رسم في السماء", "ألوان الشمس", "سحر الطبيعة"], answer: "انكسار الضوء في الماء" },
  { question: "ما أكبر محيط في العالم؟", image: "🌊", options: ["المحيط الهادئ", "المحيط الأطلسي", "المحيط الهندي", "المحيط المتجمد"], answer: "المحيط الهادئ" },
  { question: "مم يتكون الثلج؟", image: "❄️", options: ["بلورات ماء متجمد", "سكر أبيض", "ملح البحر", "غبار أبيض"], answer: "بلورات ماء متجمد" },
  { question: "ما الغاز الذي نتنفسه؟", image: "💨", options: ["الأكسجين", "ثاني أكسيد الكربون", "النيتروجين", "الهيدروجين"], answer: "الأكسجين" },
  // جسم الإنسان
  { question: "ما أكبر عضو في جسم الإنسان؟", image: "🫀", options: ["الجلد", "القلب", "الدماغ", "الكبد"], answer: "الجلد" },
  { question: "كم عظمة في جسم الإنسان البالغ؟", image: "🦴", options: ["٢٠٦ عظمة", "١٠٠ عظمة", "٣٠٠ عظمة", "٥٠ عظمة"], answer: "٢٠٦ عظمة" },
  { question: "ما وظيفة القلب؟", image: "❤️", options: ["ضخ الدم للجسم", "هضم الطعام", "التنفس", "التفكير"], answer: "ضخ الدم للجسم" },
  { question: "بماذا نشم الروائح؟", image: "👃", options: ["الأنف", "الأذن", "العين", "اللسان"], answer: "الأنف" },
  { question: "ما العضو المسؤول عن التفكير؟", image: "🧠", options: ["الدماغ", "القلب", "المعدة", "الرئة"], answer: "الدماغ" },
  // البيئة
  { question: "لماذا نعيد تدوير النفايات؟", image: "♻️", options: ["لحماية البيئة", "لأنها جميلة", "لأنها رخيصة", "ليس لها فائدة"], answer: "لحماية البيئة" },
  { question: "ما مصدر الطاقة المتجددة؟", image: "🔆", options: ["الشمس والرياح", "البترول", "الفحم", "الغاز"], answer: "الشمس والرياح" },
  { question: "لماذا نحافظ على الأشجار؟", image: "🌳", options: ["تنتج الأكسجين", "لأنها خضراء", "لأنها طويلة", "لأنها قديمة"], answer: "تنتج الأكسجين" },
];

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function ScienceNatureGame({ onComplete, difficulty = "medium" }: Props) {
  const { rounds: totalRounds, timeout } = getDifficultyConfig(difficulty);
  const [questions] = useState(() => shuffleArray([...ALL_QUESTIONS]).slice(0, totalRounds));
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const data = questions[round] || questions[0];

  const { speak, isSpeaking, isListening, startListening, stopListening, encourageCorrect, encourageWrong } = useSpeech({
    onVoiceResult: (transcript) => {
      if (selected !== null || round >= totalRounds) return;
      const normalizeAr = (s: string) => s.replace(/[^\u0600-\u06FF\s]/g, "").trim();
      const spoken = normalizeAr(transcript);
      const match = data.options.find(opt => {
        const n = normalizeAr(opt);
        return spoken.includes(n) || n.includes(spoken);
      });
      if (match) handleSelect(match);
    },
  });

  const handleSelect = (opt: string) => {
    if (selected !== null) return;
    setSelected(opt);
    if (opt === data.answer) {
      setScore((s) => s + 10);
      setStreak((s) => s + 1);
      encourageCorrect();
    } else {
      setStreak(0);
      encourageWrong();
    }
    setTimeout(() => {
      setSelected(null);
      setRound((r) => r + 1);
    }, timeout);
  };

  const reset = () => {
    setScore(0);
    setStreak(0);
    setRound(0);
    setSelected(null);
    setCompleted(false);
  };

  useEffect(() => {
    if (round >= totalRounds && !completed) {
      setCompleted(true);
      const maxScore = totalRounds * 10;
      const starCount = Math.ceil((score / maxScore) * 5);
      onComplete?.({ score, maxScore, stars: starCount });
    }
  }, [round]);

  if (round >= totalRounds) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2 }} className="text-6xl">
              🌍
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground">عالِم رائع! 🎉</h2>
            <p className="text-muted-foreground">حصلت على <span className="text-primary font-bold text-xl">{score}</span> نقطة</p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-8 h-8 ${i < Math.ceil((score / (totalRounds * 10)) * 5) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
              ))}
            </div>
            <Button onClick={reset} size="lg" className="gradient-hero border-0 text-primary-foreground">
              <RotateCcw className="w-4 h-4 ml-2" />
              اللعب مرة أخرى
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">⭐ {score} نقطة</Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">{round + 1} / {totalRounds}</Badge>
        {streak >= 2 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <Badge className="bg-gradient-to-r from-orange-400 to-red-500 text-white border-0 text-sm px-3 py-1">🔥 سلسلة {streak}</Badge>
          </motion.div>
        )}
      </div>

      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-green-400 to-emerald-600" animate={{ width: `${((round + 1) / totalRounds) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={round}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Card className="shadow-elevated overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-green-500 to-emerald-700 p-6 text-center text-white relative">
                <div className="absolute top-3 left-3 flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full" onClick={() => speak(data.question + ". الخيارات: " + data.options.join("، "))}>
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <Button variant={isListening ? "destructive" : "ghost"} size="icon" className={`h-8 w-8 rounded-full ${isListening ? "animate-pulse" : "text-white/80 hover:text-white hover:bg-white/20"}`} onClick={() => isListening ? stopListening() : startListening()} disabled={!!selected}>
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                </div>
                <motion.span className="text-7xl block mb-4" animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>{data.image}</motion.span>
                <p className="text-lg font-medium opacity-95">{data.question}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt, i) => {
          const isSelected = selected === opt;
          const showResult = selected !== null;
          const isAnswer = opt === data.answer;
          let bgClass = "bg-card hover:bg-accent border-2 border-border hover:border-primary/50";
          if (showResult && isAnswer) bgClass = "bg-emerald-100 border-2 border-emerald-500 dark:bg-emerald-900/30";
          else if (isSelected && !isAnswer) bgClass = "bg-red-100 border-2 border-red-400 dark:bg-red-900/30";

          return (
            <motion.button
              key={opt}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={!selected ? { scale: 1.05 } : {}}
              whileTap={!selected ? { scale: 0.95 } : {}}
              onClick={() => handleSelect(opt)}
              disabled={!!selected}
              className={`rounded-xl p-4 text-center transition-all ${bgClass}`}
            >
              <span className="text-base font-bold text-foreground">{opt}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
